/**
 * Tool executor
 * Main orchestrator for tool execution
 */

import type { Tool, ToolResult, ToolExecutionContext } from '../types/index.js';
import { toolRegistry } from '../registry/index.js';
import { validateToolParametersOrThrow } from './tool-validator.js';
import { withExponentialBackoff, formatToolError, type RetryConfig } from './tool-error-handler.js';

/**
 * Execute a tool
 */
export async function executeTool(
  toolId: string,
  params: Record<string, any>,
  context?: ToolExecutionContext,
  retryConfig?: Partial<RetryConfig>
): Promise<ToolResult> {
  try {
    // Get tool from registry
    const tool = toolRegistry.get(toolId);
    if (!tool) {
      return {
        type: 'error',
        error: {
          code: 'TOOL_NOT_FOUND',
          message: `Tool "${toolId}" not found`,
        },
      };
    }

    // Validate parameters
    validateToolParametersOrThrow(tool.schema, params, toolId);

    // Verify handler is initialized before execution
    if (!tool.handler) {
      return {
        type: 'error',
        error: {
          code: 'HANDLER_NOT_INITIALIZED',
          message: `Handler for tool "${toolId}" is not initialized. Please try again.`,
        },
      };
    }

    // Execute with retry
    const startTime = Date.now();
    const result = await withExponentialBackoff(
      () => tool.handler.execute(params, context),
      retryConfig
    );

    const executionTime = Date.now() - startTime;

    // Ensure result has metadata
    if (!result.metadata) {
      result.metadata = {};
    }
    result.metadata.executionTime = executionTime;
    result.metadata.toolId = toolId;

    return result;
  } catch (error) {
    const errorInfo = formatToolError(error);
    return {
      type: 'error',
      error: errorInfo,
    };
  }
}

/**
 * Get all available tools
 */
export function getAvailableTools(): Tool[] {
  return toolRegistry.list();
}

/**
 * Get tool schemas for Bedrock
 */
export function getToolSchemas(): Array<{
  name: string;
  description: string;
  inputSchema: any;
}> {
  return toolRegistry.getSchemas();
}

/**
 * Check if tool exists
 */
export function toolExists(toolId: string): boolean {
  return toolRegistry.has(toolId);
}
